//This service is used to authorize the client
import { Injectable } from "@angular/core";
import { Role } from "../classes/role";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ApiService } from "./api.service";
import { LoginStateService } from "./login-state.service";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { v4 as uuid } from "uuid";
import { tap } from "rxjs";
import { HeaderService } from "./header.service";
import { ConstantsService } from "./constants.service";
import { EventService } from "./event.service";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  public role: Role = new Role();
  private readonly ROLE_ADMIN = "ARTES_ADMIN";
  private readonly ACCESS_TOKEN = "ACCESS_TOKEN";
  private readonly REFRESH_TOKEN = "REFRESH_TOKEN";
  private readonly STATE = "STATE";
  private readonly CODE_VERIFIER = "CODE_VERIFIER";
  private validationInProgress: boolean = false;
  private validationFailed: boolean = false;

  constructor(
    private http: HttpClient,
    private api: ApiService,
    private loginState: LoginStateService,
    private router: Router,
    private headers: HeaderService,
    private constantsService: ConstantsService,
    private eventsService: EventService
  ) {}

  //Get headers for oauth server
  private getAuthHeaders(): Object {
    let headers = new HttpHeaders().append(
      "Content-Type",
      "application/x-www-form-urlencoded"
    );
    return { headers: headers };
  }

  //Local storage getters and setters
  public storeAccessToken(token: string) {
    localStorage.setItem(this.ACCESS_TOKEN, token);
  }

  public getAccessToken(): string {
    return localStorage.getItem(this.ACCESS_TOKEN);
  }

  public storeRefreshToken(token: string) {
    localStorage.setItem(this.REFRESH_TOKEN, token);
  }

  public getRefreshToken(): string {
    return localStorage.getItem(this.REFRESH_TOKEN);
  }

  public storeState(state: string) {
    localStorage.setItem(this.STATE, state);
  }

  public getState(): string {
    return localStorage.getItem(this.STATE);
  }

  //Request auth code from oauth server
  public async requestAuthCode(redirect?: string): Promise<void> {
    const state = uuid();
    const stateObj = {
      id: state,
      application: this.api.APPLICATION,
      platform: this.api.PLATFORM,
      redirect: redirect,
    };
    const encoded = btoa(JSON.stringify(stateObj));
    this.storeState(encoded);

    // Generate and store a PKCE code_verifier, then compute the code_challenge
    const code_verifier = this.generateCodeVerifier();
    // generateCodeVerifier already stores the verifier in localStorage
    const code_challenge = await this.generateCodeChallenge(code_verifier);
    const code_challenge_method = "S256";

    window.location.href = `
      ${environment.ssoUrl}/authorize?
      state=${encodeURIComponent(encoded)}&
      scope=openid&
      client_id=${encodeURIComponent(environment.clientId)}&
      redirect_uri=${encodeURIComponent(environment.redirectUrl)}&
      response_type=code&
      token_content_type=jwt&
      code_challenge=${encodeURIComponent(code_challenge)}&
      code_challenge_method=${encodeURIComponent(code_challenge_method)}
    `.replace(/\s+/g, "");
  }

  //Request access token from oauth server using auth code, redirect back to original site
  public requestAccessToken(code: string, redirect?: string): Promise<any> {
    const code_verifier = localStorage.getItem(this.CODE_VERIFIER);
    if (!code_verifier) {
      return Promise.reject(
        new Error(
          "PKCE code_verifier missing from storage. Start auth flow again."
        )
      );
    }

    const params = `
      code=${encodeURIComponent(code)}&
      redirect_uri=${encodeURIComponent(environment.redirectUrl)}&
      client_id=${encodeURIComponent(environment.clientId)}&
      grant_type=authorization_code&
      token_content_type=jwt&
      code_verifier=${encodeURIComponent(code_verifier)}
    `.replace(/\s+/g, "");

    return this.http
      .post<any>(`${environment.ssoUrl}/token`, params, this.getAuthHeaders())
      .toPromise()
      .then((response) => {
        if (response && response.id_token) {
          this.storeAccessToken(response.id_token);
          this.loginState.toggle(true);
          // PKCE verifier no longer needed after successful code exchange
          localStorage.removeItem(this.CODE_VERIFIER);
        } else this.loginState.toggle(false);
        if (response && response.refresh_token) {
          this.storeRefreshToken(response.refresh_token);
        }
        if (redirect && redirect.indexOf(window.location.origin) !== -1) {
          this.router.navigateByUrl(redirect.split(window.location.origin)[1]);
        }
        return response;
      });
  }

  //Refresh access token using refresh token
  public refreshAccessToken() {
    const params = `
      refresh_token=${encodeURIComponent(this.getRefreshToken())}&
      redirect_uri=${encodeURIComponent(environment.redirectUrl)}&
      client_id=${encodeURIComponent(environment.clientId)}&
      grant_type=refresh_token&
      token_content_type=jwt
    `.replace(/\s+/g, "");
    return this.http
      .post<any>(`${environment.ssoUrl}/token`, params, this.getAuthHeaders())
      .pipe(
        tap((response) => {
          if (response && response.id_token) {
            this.storeAccessToken(response.id_token);
            this.loginState.toggle(true);
          } else this.loginState.toggle(false);
          if (response && response.refresh_token) {
            this.storeRefreshToken(response.refresh_token);
          }
        })
      );
  }

  // Helper: base64url encode an ArrayBuffer
  private base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const b64 = btoa(binary);
    // base64url: replace +/ with -_, remove padding
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }

  // Compute the code_challenge from a code_verifier using SHA-256 and base64url encoding
  private async generateCodeChallenge(code_verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(code_verifier);
    const digest = await window.crypto.subtle.digest("SHA-256", data);
    return this.base64UrlEncode(digest);
  }

  // Generate a cryptographically-random code_verifier (PKCE) and store it
  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    // Convert to base64url
    let binary = "";
    for (let i = 0; i < array.byteLength; i++) {
      binary += String.fromCharCode(array[i]);
    }
    const b64 = btoa(binary);
    const code_verifier = b64
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    localStorage.setItem(this.CODE_VERIFIER, code_verifier);
    return code_verifier;
  }

  //Logout and delete tokens
  public logout() {
    this.storeAccessToken("");
    this.storeRefreshToken("");
    this.router.navigateByUrl("/start");
    this.loginState.toggle(false);
  }

  //Check with application server if client has valid access token
  public validateSession() {
    this.validationInProgress = true;
    let data = {
      application: this.api.APPLICATION,
      platform: this.api.PLATFORM,
    };
    return this.http
      .post<any>(this.api.VERIFY, data, this.headers.getOptions())
      .subscribe((response: any) => {
        if (response && response.role) {
          this.validationFailed = false;
          this.role = new Role(response.role);
          if (this.constantsService.wasLoaded === false && this.isAdmin) {
            //this.constantsService.getConstants();
            this.eventsService.getScans();
            this.eventsService.getReports();
          }
        } else {
          this.validationFailed = true;
        }
      });
  }

  //Check if user has administrator role
  public get isAdmin(): Boolean {
    if (
      !this.validationInProgress &&
      !this.validationFailed &&
      !localStorage.getItem("role")
    ) {
      this.validateSession();
    }
    return (
      this.role.role === this.ROLE_ADMIN ||
      localStorage.getItem("role") === this.ROLE_ADMIN
    );
  }
}
